/*
 * Copyright (c) 2025 BuyerView.ai
 * All rights reserved.
 * Unauthorized copying prohibited.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */


/**
 * Google Analytics 4 Integration Service
 *
 * Provides comprehensive analytics tracking:
 * - Page views and user sessions
 * - Custom events and conversions
 * - E-commerce tracking
 * - User behavior analysis
 * - Integration with our custom monitoring
 */

// Declare gtag function for TypeScript
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

const gtag = (...args: any[]) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag(...args);
  }
};

// Google Analytics Configuration
const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID || 'G-XXXXXXXXXX';
const GA_DEBUG_MODE = import.meta.env.DEV;

// Custom Events for BuyerView.ai
export const GA_EVENTS = {
  // User Actions
  SIGN_UP: 'sign_up',
  LOGIN: 'login',
  REVIEW_SUBMITTED: 'review_submitted',
  LOCATION_VERIFIED: 'location_verified',

  // Search & Discovery
  PROPERTY_SEARCH: 'property_search',
  PROPERTY_VIEW: 'property_view',
  REVIEW_VIEW: 'review_view',

  // Agent Interactions
  AGENT_REPLY_VIEW: 'agent_reply_view',
  AGENT_CONTACT: 'agent_contact',

  // Performance & UX
  PERFORMANCE_ISSUE: 'performance_issue',
  ERROR_OCCURRED: 'error_occurred',
  AB_TEST_CONVERSION: 'ab_test_conversion',

  // Business Metrics
  REVIEW_COMPLETION: 'review_completion',
  VERIFICATION_COMPLETION: 'verification_completion',
  USER_ENGAGEMENT: 'user_engagement'
};

// User Properties for Segmentation
export const GA_USER_PROPERTIES = {
  USER_TYPE: 'user_type', // 'buyer', 'agent', 'anonymous'
  VERIFICATION_STATUS: 'verification_status',
  REVIEW_COUNT: 'review_count',
  AB_TEST_VARIANTS: 'ab_test_variants'
};

interface GAEvent {
  action: string;
  category?: string;
  label?: string;
  value?: number;
  custom_parameters?: Record<string, any>;
}

interface GAUserProperties {
  user_type?: 'buyer' | 'agent' | 'anonymous';
  verification_status?: 'verified' | 'pending' | 'unverified';
  review_count?: number;
  ab_test_variants?: string;
}

class GoogleAnalyticsService {
  private isInitialized = false;
  private userId?: string;

  /**
   * Initialize Google Analytics
   */
  async initialize(): Promise<void> {
    if (this.isInitialized || !GA_MEASUREMENT_ID || GA_MEASUREMENT_ID === 'G-XXXXXXXXXX') {
      console.log('📊 GA not initialized:', {
        hasId: !!GA_MEASUREMENT_ID,
        validId: GA_MEASUREMENT_ID !== 'G-XXXXXXXXXX',
        isInitialized: this.isInitialized
      });
      return;
    }

    try {
      // Load Google Analytics script
      await this.loadGAScript();

      // Configure Google Analytics
      gtag('config', GA_MEASUREMENT_ID, {
        debug_mode: GA_DEBUG_MODE,
        send_page_view: false, // We'll handle page views manually
        anonymize_ip: true, // GDPR compliance
        allow_google_signals: false, // Disable advertising features initially
        cookie_flags: GA_DEBUG_MODE ? 'SameSite=Strict' : 'SameSite=Strict;Secure', // Allow non-HTTPS in dev
        custom_map: {
          'custom_parameter_1': 'development_mode'
        }
      });

      this.isInitialized = true;
      console.log('📊 Google Analytics initialized:', GA_MEASUREMENT_ID);

      // Track initial page view
      this.trackPageView();

    } catch (error) {
      console.error('Failed to initialize Google Analytics:', error);
    }
  }

  /**
   * Set user ID for tracking
   */
  setUserId(userId: string, userProperties?: GAUserProperties): void {
    this.userId = userId;

    if (this.isInitialized) {
      gtag('config', GA_MEASUREMENT_ID, {
        user_id: userId
      });

      // Set user properties for segmentation
      if (userProperties) {
        this.setUserProperties(userProperties);
      }

      console.log('👤 GA User ID set:', userId);
    }
  }

  /**
   * Set user properties for advanced segmentation
   */
  setUserProperties(properties: GAUserProperties): void {
    if (!this.isInitialized) return;

    const gaProperties: Record<string, any> = {};

    Object.entries(properties).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        gaProperties[key] = value;
      }
    });

    gtag('set', 'user_properties', gaProperties);
    console.log('🏷️ GA User properties set:', gaProperties);
  }

  /**
   * Track page view
   */
  trackPageView(url?: string, title?: string): void {
    if (!this.isInitialized) return;

    const pageUrl = url || window.location.pathname + window.location.search;
    const pageTitle = title || document.title;

    gtag('event', 'page_view', {
      page_title: pageTitle,
      page_location: window.location.href,
      page_path: pageUrl,
      send_to: GA_MEASUREMENT_ID
    });

    console.log('📄 GA Page view:', pageUrl);
  }

  /**
   * Track custom event
   */
  trackEvent(event: GAEvent): void {
    if (!this.isInitialized) return;

    const eventData: Record<string, any> = {
      event_category: event.category || 'user_action',
      event_label: event.label,
      value: event.value,
      send_to: GA_MEASUREMENT_ID,
      ...event.custom_parameters
    };

    // Clean up undefined values
    Object.keys(eventData).forEach(key => {
      if (eventData[key] === undefined) {
        delete eventData[key];
      }
    });

    gtag('event', event.action, eventData);
    console.log('🎯 GA Event tracked:', event.action, eventData);
  }

  /**
   * Track user signup
   */
  trackSignup(method: string = 'email'): void {
    this.trackEvent({
      action: GA_EVENTS.SIGN_UP,
      category: 'authentication',
      custom_parameters: {
        method: method,
        timestamp: new Date().toISOString()
      }
    });
  }

  /**
   * Track user login
   */
  trackLogin(method: string = 'email'): void {
    this.trackEvent({
      action: GA_EVENTS.LOGIN,
      category: 'authentication',
      custom_parameters: {
        method: method,
        timestamp: new Date().toISOString()
      }
    });
  }

  /**
   * Track review submission
   */
  trackReviewSubmission(propertyType?: string, reviewType?: string): void {
    this.trackEvent({
      action: GA_EVENTS.REVIEW_SUBMITTED,
      category: 'engagement',
      custom_parameters: {
        property_type: propertyType,
        review_type: reviewType,
        timestamp: new Date().toISOString()
      }
    });
  }

  /**
   * Track property search
   */
  trackPropertySearch(searchQuery: string, resultsCount?: number): void {
    this.trackEvent({
      action: GA_EVENTS.PROPERTY_SEARCH,
      category: 'search',
      label: searchQuery,
      value: resultsCount,
      custom_parameters: {
        search_term: searchQuery,
        results_count: resultsCount,
        timestamp: new Date().toISOString()
      }
    });
  }

  /**
   * Track A/B test conversion
   */
  trackABTestConversion(experimentId: string, variantId: string, conversionType: string): void {
    this.trackEvent({
      action: GA_EVENTS.AB_TEST_CONVERSION,
      category: 'ab_testing',
      label: `${experimentId}:${variantId}`,
      custom_parameters: {
        experiment_id: experimentId,
        variant_id: variantId,
        conversion_type: conversionType,
        timestamp: new Date().toISOString()
      }
    });
  }

  /**
   * Track performance issues
   */
  trackPerformanceIssue(metricName: string, value: number, threshold: number): void {
    this.trackEvent({
      action: GA_EVENTS.PERFORMANCE_ISSUE,
      category: 'performance',
      label: metricName,
      value: Math.round(value),
      custom_parameters: {
        metric_name: metricName,
        actual_value: value,
        threshold: threshold,
        severity: value > threshold * 2 ? 'high' : 'medium',
        timestamp: new Date().toISOString()
      }
    });
  }

  /**
   * Track errors
   */
  trackError(errorType: string, errorMessage: string, severity: string = 'medium'): void {
    this.trackEvent({
      action: GA_EVENTS.ERROR_OCCURRED,
      category: 'errors',
      label: errorType,
      custom_parameters: {
        error_type: errorType,
        error_message: errorMessage.substring(0, 100), // Limit message length
        severity: severity,
        page_url: window.location.href,
        timestamp: new Date().toISOString()
      }
    });
  }

  /**
   * Track location verification
   */
  trackLocationVerification(method: string, success: boolean): void {
    this.trackEvent({
      action: GA_EVENTS.LOCATION_VERIFIED,
      category: 'verification',
      label: method,
      value: success ? 1 : 0,
      custom_parameters: {
        verification_method: method,
        success: success,
        timestamp: new Date().toISOString()
      }
    });
  }

  /**
   * Track user engagement (time on page, interactions)
   */
  trackEngagement(engagementTime: number, interactionCount: number): void {
    this.trackEvent({
      action: GA_EVENTS.USER_ENGAGEMENT,
      category: 'engagement',
      value: Math.round(engagementTime),
      custom_parameters: {
        engagement_time_msec: engagementTime,
        interaction_count: interactionCount,
        page_url: window.location.href,
        timestamp: new Date().toISOString()
      }
    });
  }

  /**
   * Create conversion goal
   */
  trackConversion(goalName: string, value?: number): void {
    this.trackEvent({
      action: 'conversion',
      category: 'goals',
      label: goalName,
      value: value,
      custom_parameters: {
        goal_name: goalName,
        conversion_value: value,
        timestamp: new Date().toISOString()
      }
    });
  }

  // Private helper methods
  private async loadGAScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if script is already loaded
      if (window.gtag) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;

      script.onload = () => {
        // Initialize gtag
        window.dataLayer = window.dataLayer || [];
        function gtag(...args: any[]) {
          window.dataLayer.push(args);
        }
        window.gtag = gtag;

        gtag('js', new Date());
        resolve();
      };

      script.onerror = () => {
        reject(new Error('Failed to load Google Analytics script'));
      };

      document.head.appendChild(script);
    });
  }
}

// Export singleton instance
export const googleAnalytics = new GoogleAnalyticsService();

// Auto-initialize in browser environment
if (typeof window !== 'undefined') {
  googleAnalytics.initialize();
}